import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { formatReadableAmount } from '../../../Common/FormatFunctions/FormatAmount';
import { ScaleLoader } from 'react-spinners';
import Swal from 'sweetalert2';
import { PostDateRecordLock } from '../../../Common/PostDateLock/PostDateRangeCheck';
import { validateDocumentDate } from '../../../Common/ValidateDateRange/ValidateDateRange';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableFooter, TablePagination, Typography, Box, Button, FormControl,
    Select, MenuItem, Dialog, Slide, CircularProgress,
} from '@mui/material';
import PageNotFound from '../../../Common/PageNotFound/PageNotFound';
import useProgramPermissions from '../../../Common/Permissions/useProgramPermissions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import messageTemplates from '../../../Common/MessageData/data.json';
import BackButton from '../../../Common/Buttons/BackButton';
import { useNavigate } from 'react-router-dom';

/* ───────────────────────── helpers ───────────────────────── */

const money = (v) =>
    (Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Keeps the last 10 digits and adds 91. Returns '' if the number is unusable.
const cleanMobile = (v) => {
    const digits = String(v || '').replace(/\D/g, '');
    return digits.length >= 10 ? '91' + digits.slice(-10) : '';
};

// Replaces {key} with its value; empty values become '-' (WhatsApp rejects empty params)
const fillTemplate = (str = '', data) =>
    str.replace(/\{(\w+)\}/g, (_, k) =>
        data[k] !== undefined && data[k] !== null && String(data[k]).trim() !== '' ? String(data[k]) : '-'
    );

const apikey = process.env.REACT_APP_API;

const RECEIPT_TRAN_TYPE = 'BP';

// Bank account to use, keyed by Group_Code. gcid is the expected companion value
// (from nt_1_systemmaster) — used to sanity-check the row.
const BANK_BY_GROUP = {
    1: { gcid: 71, cashbank: 11192, cb: 10459, label: 'JK Commodities' },
    2: { gcid: 72, cashbank: 13425, cb: 13710, label: 'Chinimandi' },
    3: { gcid: 7, cashbank: 13424, cb: 13709, label: 'Agrimandi' },
};

const getBankConfig = (item) => {
    const config = BANK_BY_GROUP[Number(item.Group_Code)];
    if (!config) return null;
    if (Number(item.gcid) !== config.gcid) return null;
    return config;
};

// Local date as YYYY-MM-DD (toISOString gives the UTC date, which can be a day behind)
const getLocalDate = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// SQL CONVERT(...,103) gives dd/mm/yyyy — the bank API needs yyyy-mm-dd
const toISODate = (v) => {
    const s = String(v || '').slice(0, 10);
    const [d, m, y] = s.split('/');
    return d && m && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : s;
};

const SCREEN_COLUMNS = [
    { label: 'Doc Date', key: 'Doc_Date', width: '9%' },
    { label: 'Supplier', key: 'SupplierName', width: '15%' },
    { label: 'Group', key: 'System_Name_E', width: '8%' },
    { label: 'Type', key: 'tran_type', width: '9%' },
    { label: 'Narration', key: 'OPnarration', width: '13%' },
    { label: 'Value Date', key: 'Value_date', width: '8%' },
    { label: 'Bill Amount', key: 'Bill_Amount', width: '8%', numeric: true },
    { label: 'Pay Amount', key: 'amount', width: '8%', numeric: true },
    { label: 'Remark', key: 'remark', width: '9%' },
    { label: 'Bank Details', key: 'bankdetails', width: '12%', isInfo: true },
    { label: 'Status', key: 'paystatus', width: '10%', isStatus: true },
    { label: 'Action', key: 'action', width: '9%', isStatus: true },
];

const PRINT_COLUMNS = SCREEN_COLUMNS.filter((c) => !c.isStatus && !c.isInput && !c.isInfo);

// Required in the account master before a vendor can be paid via the bank API
const BENE_REQUIRED_FIELDS = [
    { key: 'Bank_Ac_No', label: 'Account Number' },
    { key: 'IFSC', label: 'IFSC Code' },
    { key: 'Bank_Name', label: 'Bank Name' },
];

const getMissingBeneFields = (item) =>
    BENE_REQUIRED_FIELDS.filter((f) => !String(item?.[f.key] || '').trim()).map((f) => f.label);

// value = our internal payment_mode code (stored/notified); txnPaymode = the exact code Axis Bank expects
const PAYMENT_MODES = [
    { value: 'N', label: 'NEFT', txnPaymode: 'NE' },
    { value: 'R', label: 'RTGS', txnPaymode: 'RT' },
    { value: 'I', label: 'IMPS', txnPaymode: 'PA' },
    { value: 'U', label: 'UPI', txnPaymode: 'UP' },
    { value: 'C', label: 'Cheque' },
];

const BANK_PAY_MODES = PAYMENT_MODES.filter((m) => m.txnPaymode);

// Axis Bank's beneAcType codes
const BENE_AC_TYPES = [
    { value: '10', label: '10 - Savings Bank' },
    { value: '11', label: '11 - Current Account' },
    { value: '13', label: '13 - Cash Credit' },
    { value: '40', label: '40 - NRE' },
    { value: '41', label: '41 - NRO' },
];

const TRAN_TYPE_LABELS = {
    OP: 'Other Purchase',
    AD: 'Advance',
    IP: 'Interest Paid',
};

const tranTypeLabel = (code) =>
    TRAN_TYPE_LABELS[String(code || '').trim().toUpperCase()] || code || '';

// dd/mm/yyyy or yyyy-mm-dd → yyyy-mm-dd (for compare / sort)
const normDate = (v) => {
    const s = String(v || '').slice(0, 10);
    return s.includes('/') ? toISODate(s) : s;
};

const groupAccountNo = (v) => String(v || '').replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();

/* ───────────────────────── icons (no extra deps) ───────────────────────── */

const Svg = ({ children, size = 20, color = 'currentColor', sw = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw}
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IconCheck = (p) => <Svg {...p}><path d="M5 12.5l4.5 4.5L19 7.5" /></Svg>;
const IconClose = (p) => <Svg {...p}><path d="M6 6l12 12M18 6L6 18" /></Svg>;
const IconClock = (p) => <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Svg>;
const IconBank = (p) => <Svg {...p}><path d="M3 10l9-6 9 6M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" /></Svg>;
const IconShield = (p) => <Svg {...p}><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" /><path d="M9 12l2 2 4-4" /></Svg>;
const IconFilter = (p) => <Svg {...p}><path d="M4 5h16l-6 8v6l-4-2v-4L4 5z" /></Svg>;
const IconCopy = (p) => <Svg {...p}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 012-2h9" /></Svg>;
const IconArrow = (p) => <Svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></Svg>;
const IconSearch = (p) => <Svg {...p}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></Svg>;
const IconInbox = (p) => <Svg {...p}><path d="M4 13l2-8h12l2 8M4 13v6h16v-6M4 13h5l1 2h4l1-2h5" /></Svg>;

/* ───────────────────────── styles ───────────────────────── */

const PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Signika:wght@300;400;500;600;700&display=swap');
:root {
  --pg-ink:#1c1f3d; --pg-mute:#6f7496; --pg-line:#e8eaf5; --pg-bg:#f3f4fb; --pg-card:#fff;
  --pg-brand:#5146e5; --pg-brand-2:#8467ff; --pg-brand-soft:#eff0ff;
  --pg-ok:#0f8a60; --pg-ok-soft:#e1f6ec; --pg-warn:#b36b00; --pg-warn-soft:#fff2d9;
  --pg-bad:#c8352f; --pg-bad-soft:#fde8e7;
  --pg-font:'Signika','Segoe UI',system-ui,sans-serif;
}
.pg-page, .pg-page *, .pg-dlg, .pg-dlg * { font-family:var(--pg-font); }
.pg-page { background:var(--pg-bg); color:var(--pg-ink); border-radius:14px; min-height:100vh; }
.pg-page .MuiTableCell-root, .pg-page .MuiInputBase-root, .pg-page .MuiTablePagination-root, .pg-page .MuiTablePagination-root * { font-family:var(--pg-font) !important; }

.pg-head { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:14px; flex-wrap:wrap; }
.pg-head-left { display:flex; align-items:center; gap:14px; }
.pg-title { font-size:24px; font-weight:600; letter-spacing:-.01em; margin:0; line-height:1.15; }
.pg-sub { font-size:13.5px; color:var(--pg-mute); margin:3px 0 0; font-weight:400; }

.pg-btn { display:inline-flex; align-items:center; justify-content:center; gap:7px; padding:9px 16px; border-radius:12px; font-size:14px; font-weight:500; line-height:1.2; text-transform:none; letter-spacing:normal; white-space:nowrap; cursor:pointer; border:1px solid var(--pg-line) !important; background:#fff !important; color:var(--pg-ink) !important; box-shadow:none; transition:background .2s, border-color .2s, transform .15s, box-shadow .2s; }
.pg-btn:hover { background:var(--pg-brand-soft) !important; border-color:#cdd1ff !important; }
.pg-btn:active { transform:scale(.97); }
.pg-btn:focus-visible { outline:2px solid var(--pg-brand); outline-offset:2px; }
.pg-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
.pg-btn-primary { background:linear-gradient(135deg,var(--pg-brand),var(--pg-brand-2)) !important; color:#fff !important; border-color:transparent !important; box-shadow:0 8px 20px rgba(81,70,229,.3); }
.pg-btn-primary:hover { background:linear-gradient(135deg,#4338d8,#7655f5) !important; box-shadow:0 10px 24px rgba(81,70,229,.38); }
.pg-btn-ghost { border-color:transparent !important; background:transparent !important; color:var(--pg-brand) !important; padding:5px 10px; font-size:13px; }
.pg-btn-ghost:hover { background:var(--pg-brand-soft) !important; }

/* ---- main card, tabs, filters ---- */
.pg-card { background:var(--pg-card); border:1px solid var(--pg-line); border-radius:18px; overflow:hidden; box-shadow:0 2px 8px rgba(28,31,61,.04); }
.pg-toolbar { display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; padding:14px 18px 0; }
.pg-tabs { display:flex; gap:4px; border-bottom:1px solid var(--pg-line); flex:1 1 auto; overflow-x:auto; }
.pg-tab { position:relative; display:inline-flex; align-items:center; gap:8px; padding:10px 14px 12px; border:0 !important; background:transparent !important; font-size:14.5px; font-weight:500; color:var(--pg-mute) !important; cursor:pointer; white-space:nowrap; transition:color .2s; }
.pg-tab:hover { color:var(--pg-ink) !important; }
.pg-tab::after { content:''; position:absolute; left:10px; right:10px; bottom:-1px; height:3px; border-radius:3px 3px 0 0; background:var(--pg-brand); transform:scaleX(0); transition:transform .3s cubic-bezier(.2,.8,.2,1); }
.pg-tab-on { color:var(--pg-brand) !important; font-weight:600; }
.pg-tab-on::after { transform:scaleX(1); }
.pg-count { min-width:22px; height:20px; padding:0 7px; border-radius:10px; background:#eceefa; color:var(--pg-mute); font-size:12px; font-weight:600; display:inline-flex; align-items:center; justify-content:center; transition:background .2s, color .2s; }
.pg-tab-on .pg-count { background:var(--pg-brand); color:#fff; }
.pg-search { position:relative; width:290px; max-width:100%; margin-bottom:6px; }
.pg-search svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--pg-mute); pointer-events:none; }
.pg-search input { width:100%; height:40px; padding:0 12px 0 38px; border:1px solid var(--pg-line); border-radius:12px; font-size:14px; color:var(--pg-ink); background:#fafbff; outline:none; transition:border-color .2s, box-shadow .2s, background .2s; }
.pg-search input:focus { border-color:var(--pg-brand); background:#fff; box-shadow:0 0 0 4px rgba(81,70,229,.12); }

.pg-frow { display:flex; align-items:center; gap:10px 18px; flex-wrap:wrap; padding:12px 18px; border-bottom:1px solid var(--pg-line); background:#fcfcff; }
.pg-ftitle { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:var(--pg-mute); }
.pg-fsel { display:inline-flex; align-items:center; gap:8px; }
.pg-fsel label { margin:0; font-size:13px; color:var(--pg-mute); font-weight:400; }
.pg-fchips { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-left:auto; }
.pg-fchip { display:inline-flex; align-items:center; gap:6px; padding:4px 6px 4px 11px; border-radius:999px; background:var(--pg-brand-soft); color:var(--pg-brand); font-size:12.5px; font-weight:500; animation:pgChip .25s cubic-bezier(.2,.9,.3,1.2) both; }
.pg-fchip button { width:18px; height:18px; border-radius:50%; border:0 !important; background:rgba(81,70,229,.16) !important; color:var(--pg-brand) !important; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; padding:0; }
.pg-fchip button:hover { background:var(--pg-brand) !important; color:#fff !important; }
.pg-tmeta { padding:10px 18px; font-size:13px; color:var(--pg-mute); }

/* ---- table ---- */
.pg-table { min-width:1450px; }
.pg-table th { background:#fff !important; color:var(--pg-mute) !important; font-size:13px !important; font-weight:600 !important; padding:12px 12px !important; border-bottom:1px solid var(--pg-line) !important; white-space:nowrap; user-select:none; }
.pg-table th.pg-sortable { cursor:pointer; }
.pg-table th.pg-sortable:hover, .pg-table th.pg-sorted { color:var(--pg-brand) !important; }
.pg-sort { margin-left:5px; font-size:12px; opacity:.4; }
.pg-sorted .pg-sort { opacity:1; }
.pg-table td { padding:12px !important; font-size:14px !important; border-bottom:1px solid #f0f1f9 !important; vertical-align:middle; color:var(--pg-ink); }
.pg-table tbody tr { transition:background .15s; }
.pg-table tbody tr:hover td { background:#f7f8ff; }
.pg-table tfoot td { background:#fafbff !important; font-weight:600 !important; border-top:1px solid var(--pg-line) !important; border-bottom:0 !important; font-size:14px !important; }
.pg-num { font-variant-numeric:tabular-nums; font-weight:600 !important; }
.pg-strong { font-weight:600; }
.pg-muted { color:var(--pg-mute); font-size:12.5px; }
.pg-sup { display:flex; align-items:center; gap:10px; }
.pg-av { width:34px; height:34px; border-radius:11px; background:linear-gradient(135deg,#e7e5ff,#d5ccff); color:var(--pg-brand); font-size:12.5px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; flex:none; }
.pg-supname { font-weight:500; line-height:1.25; }
.pg-clamp { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; max-width:250px; }
.pg-pill { display:inline-flex; align-items:center; gap:6px; padding:4px 11px; border-radius:999px; font-size:12.5px; font-weight:500; white-space:nowrap; }
.pg-pill i { width:6px; height:6px; border-radius:50%; background:currentColor; }
.pg-pill-ok { background:var(--pg-ok-soft); color:var(--pg-ok); }
.pg-pill-warn { background:var(--pg-warn-soft); color:var(--pg-warn); }
.pg-pill-bad { background:var(--pg-bad-soft); color:var(--pg-bad); }
.pg-pill-info { background:var(--pg-brand-soft); color:var(--pg-brand); }
.pg-pill-btn { cursor:pointer; border:0 !important; }
.pg-bank { line-height:1.45; }
.pg-rowbtn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px; font-size:13px; font-weight:500; line-height:1.2; white-space:nowrap; cursor:pointer; border:1px solid transparent !important; transition:transform .15s, background .2s, box-shadow .2s; }
.pg-rowbtn:active { transform:scale(.96); }
.pg-rowbtn-primary { background:var(--pg-brand) !important; color:#fff !important; }
.pg-rowbtn-primary:hover { background:#4338d8 !important; box-shadow:0 6px 14px rgba(81,70,229,.3); }
.pg-rowbtn-outline { background:#fff !important; border-color:#cdd1ff !important; color:var(--pg-brand) !important; }
.pg-rowbtn-outline:hover { background:var(--pg-brand-soft) !important; }
.pg-empty { text-align:center; padding:54px 12px; color:var(--pg-mute); }
.pg-empty b { display:block; color:var(--pg-ink); margin:10px 0 2px; font-size:16px; font-weight:600; }

.pg-bar { position:sticky; bottom:0; z-index:20; margin-top:12px; background:#fff; border:1px solid var(--pg-line); border-radius:16px; padding:12px 18px; display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; box-shadow:0 -8px 24px rgba(28,31,61,.07); }
.pg-stats { display:flex; gap:30px; flex-wrap:wrap; }
.pg-stat span { display:block; font-size:12.5px; color:var(--pg-mute); }
.pg-stat b { font-size:18px; font-weight:600; font-variant-numeric:tabular-nums; }
.pg-stat-sel b { color:var(--pg-brand); }

/* ---- Make Payment popup (single card) ---- */
.pg-dlg .MuiBackdrop-root { background:rgba(24,20,70,.5); backdrop-filter:blur(5px); }
.pg-dlg .MuiDialog-paper { border-radius:30px; max-width:1180px; width:100%; margin:12px; max-height:86vh; overflow-x:hidden; overflow-y:auto; box-shadow:0 40px 90px rgba(30,25,110,.4); }
.pg-rise { animation:pgRise .6s cubic-bezier(.2,.8,.2,1) both; animation-delay:var(--d,0ms); }

.pg-sh { position:relative; overflow:hidden; background:linear-gradient(150deg,#2b22bf 0%,#5443f5 55%,#8d6dff 100%); color:#fff; padding:14px 28px 14px; }
.pg-blob { position:absolute; border-radius:50%; pointer-events:none; }
.pg-blob-a { width:300px; height:300px; right:-90px; top:-120px; background:radial-gradient(circle,rgba(255,255,255,.24),transparent 70%); animation:pgFloat 9s ease-in-out infinite alternate; }
.pg-blob-b { width:280px; height:280px; left:-100px; bottom:-150px; background:radial-gradient(circle,rgba(255,255,255,.16),transparent 70%); animation:pgFloat 11s ease-in-out infinite alternate-reverse; }
.pg-sh > * { position:relative; }
.pg-lbl { font-size:13.5px; opacity:.85; }
.pg-amt { font-size:38px; font-weight:600; letter-spacing:-.02em; line-height:1.15; margin:2px 0 0; font-variant-numeric:tabular-nums; }
.pg-lsub { font-size:15.5px; font-weight:500; opacity:.95; }
.pg-metas { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
.pg-meta { padding:4px 12px; border-radius:999px; background:rgba(255,255,255,.17); border:1px solid rgba(255,255,255,.3); font-size:12.5px; }
.pg-x { position:absolute !important; z-index:10; top:14px; right:14px; width:36px; height:36px; border-radius:50%; border:0 !important; background:rgba(255,255,255,.18) !important; color:#fff !important; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background .2s, transform .2s; padding:0; }
.pg-x:hover { background:rgba(255,255,255,.32) !important; transform:rotate(90deg); }
.pg-x:disabled { opacity:.4; cursor:not-allowed; transform:none; }

.pg-sb { padding:12px 28px 14px; display:flex; flex-direction:column; gap:10px; background:#fff; }
.pg-sec { font-size:13px; font-weight:600; color:var(--pg-mute); margin:0 0 6px; }

.pg-banks { display:grid; grid-template-columns:1fr 70px 1fr; align-items:stretch; }
.pg-banks .pg-hflow { align-self:center; }
@media (max-width:780px) { .pg-banks { grid-template-columns:1fr; gap:14px; } .pg-hflow { display:none; } }
.pg-bcard { background:#f8f9ff; border:1px solid var(--pg-line); border-radius:16px; padding:14px 16px; }
.pg-bcard-to { background:linear-gradient(135deg,#f3f2ff,#e9efff); border-color:#cfd3ff; }
.pg-bcard-warn { background:var(--pg-bad-soft); border-color:#f3b4b1; }
.pg-bcard-top { display:flex; align-items:center; justify-content:space-between; font-size:12.5px; color:var(--pg-mute); margin-bottom:6px; gap:10px; }
.pg-bcard-name { font-size:16px; font-weight:600; line-height:1.25; }
.pg-bcard-sub { font-size:13px; color:var(--pg-mute); margin-top:3px; }
.pg-bcard-ac { font-size:20px; letter-spacing:.1em; font-weight:500; margin:6px 0 8px; font-variant-numeric:tabular-nums; }
.pg-bcard-meta { display:flex; gap:22px; font-size:12px; color:var(--pg-mute); }
.pg-bcard-meta b { display:block; font-size:14px; font-weight:600; color:var(--pg-ink); }
.pg-chipimg { width:34px; height:25px; border-radius:6px; background:linear-gradient(135deg,#ffe8a6,#eeb54a); box-shadow:inset 0 0 0 1px rgba(0,0,0,.12); }

.pg-hflow { position:relative; height:2px; margin:0 10px; background:repeating-linear-gradient(to right,#c4c8f5 0 5px,transparent 5px 10px); }
.pg-hflow span { position:absolute; top:-4px; left:0; width:10px; height:10px; border-radius:50%; background:var(--pg-brand); box-shadow:0 0 10px rgba(81,70,229,.6); animation:pgGo 2.4s ease-in-out infinite; }
.pg-hflow span:nth-child(2) { animation-delay:1.2s; }
.pg-flow-go span { animation-duration:.9s; }
.pg-flow-go span:nth-child(2) { animation-delay:.45s; }
.pg-flow-done span, .pg-flow-off span { animation:none; opacity:0; }
.pg-flow-done { background:var(--pg-ok); height:3px; }
.pg-flow-off { opacity:.3; }

.pg-grid2 { display:grid; grid-template-columns:1fr 1fr 1fr; column-gap:28px; border:1px solid var(--pg-line); border-radius:16px; padding:2px 20px; background:#fcfcff; }
@media (max-width:1040px) { .pg-grid2 { grid-template-columns:1fr 1fr; } }
@media (max-width:780px) { .pg-grid2 { grid-template-columns:1fr; } }
.pg-row { display:flex; justify-content:space-between; gap:16px; padding:8px 0; border-bottom:1px dashed var(--pg-line); font-size:14px; }
.pg-row span { color:var(--pg-mute); flex:none; }
.pg-row b { font-weight:500; text-align:right; word-break:break-word; }
.pg-row-net { grid-column:1 / -1; border-bottom:0; align-items:center; font-size:15px; }
.pg-row-net span { color:var(--pg-ink); font-weight:600; }
.pg-row-net b { color:var(--pg-brand); font-weight:700; font-size:19px; font-variant-numeric:tabular-nums; }
.pg-secure { display:flex; align-items:center; justify-content:center; gap:7px; font-size:12px; color:var(--pg-mute); margin-top:-4px; }

.pg-prog { height:7px; background:#eceefa; border-radius:7px; overflow:hidden; }
.pg-prog i { display:block; height:100%; border-radius:7px; background:linear-gradient(90deg,var(--pg-brand),var(--pg-brand-2)); transition:width .8s cubic-bezier(.2,.8,.2,1), background .4s; }
.pg-prog-ok i { background:linear-gradient(90deg,#0f8a60,#34d399); }
.pg-prog-warn i { background:linear-gradient(90deg,#d98a00,#fbbf24); }
.pg-prog-bad i { background:linear-gradient(90deg,#c8352f,#f87171); }
.pg-prog-lbl { display:flex; justify-content:space-between; margin-top:8px; font-size:12.5px; color:var(--pg-mute); }
.pg-prog-lbl .on { color:var(--pg-brand); font-weight:600; }

.pg-res { display:flex; align-items:center; gap:14px; padding:10px 14px; border-radius:18px; animation:pgPop .5s cubic-bezier(.2,.9,.3,1.2) both; }
.pg-res b { display:block; font-size:16px; font-weight:600; }
.pg-res span.t { font-size:13.5px; word-break:break-word; opacity:.9; }
.pg-res-busy { background:var(--pg-brand-soft); }
.pg-res-ok { background:var(--pg-ok-soft); }
.pg-res-warn { background:var(--pg-warn-soft); }
.pg-res-bad { background:var(--pg-bad-soft); animation:pgPop .5s cubic-bezier(.2,.9,.3,1.2) both, pgShake .5s .15s; }
.pg-ico { width:46px; height:46px; flex:none; display:flex; align-items:center; justify-content:center; position:relative; }
.pg-ico-c { width:46px; height:46px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; }
.pg-res-ok .pg-ico-c { background:var(--pg-ok); }
.pg-res-warn .pg-ico-c { background:var(--pg-warn); }
.pg-res-bad .pg-ico-c { background:var(--pg-bad); }
.pg-res-ok .pg-ico-c::after { content:''; position:absolute; inset:0; border-radius:50%; border:2px solid var(--pg-ok); animation:pgRing 1.1s ease-out .2s both; }
.pg-tick path { stroke-dasharray:30; stroke-dashoffset:30; animation:pgDraw .55s .25s ease-out forwards; }
.pg-pulse::before, .pg-pulse::after { content:''; position:absolute; inset:6px; border-radius:50%; border:2px solid var(--pg-brand); animation:pgRing 1.6s ease-out infinite; }
.pg-pulse::after { animation-delay:.8s; }
.pg-clock-hand { transform-origin:12px 12px; animation:pgSpin 3s linear infinite; }
.pg-copy { margin-left:auto; display:inline-flex; align-items:center; gap:6px; padding:6px 11px; border-radius:10px; border:1px solid rgba(15,138,96,.35) !important; background:#fff !important; color:var(--pg-ok) !important; font-size:12.5px; font-weight:500; cursor:pointer; flex:none; }
.pg-note { font-size:13px; color:var(--pg-mute); margin:-4px 0 0; }

.pg-actions { display:flex; gap:10px; margin-top:auto; padding-top:4px; }
.pg-actions .pg-btn { flex:1; padding:11px 16px; font-size:14.5px; border-radius:13px; }
.pg-actions .pg-btn-primary { flex:1.6; }

@keyframes pgRise { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
@keyframes pgPop { from { opacity:0; transform:scale(.92); } to { opacity:1; transform:none; } }
@keyframes pgChip { from { opacity:0; transform:scale(.8); } to { opacity:1; transform:none; } }
@keyframes pgFloat { from { transform:translate(0,0); } to { transform:translate(-18px,16px); } }
@keyframes pgGo { 0% { left:0; opacity:0; } 15% { opacity:1; } 85% { opacity:1; } 100% { left:calc(100% - 10px); opacity:0; } }
@keyframes pgDrop { 0% { top:0; opacity:0; } 15% { opacity:1; } 85% { opacity:1; } 100% { top:30px; opacity:0; } }
@keyframes pgRing { 0% { transform:scale(.8); opacity:.8; } 100% { transform:scale(1.7); opacity:0; } }
@keyframes pgDraw { to { stroke-dashoffset:0; } }
@keyframes pgSpin { to { transform:rotate(360deg); } }
@keyframes pgShake { 0%,100% { transform:translateX(0); } 20% { transform:translateX(-7px); } 40% { transform:translateX(6px); } 60% { transform:translateX(-4px); } 80% { transform:translateX(3px); } }
@media (prefers-reduced-motion:reduce) { .pg-dlg *, .pg-page * { animation:none !important; transition:none !important; } }
`;

/* ───────────────────────── small components ───────────────────────── */

const FONT = "'Signika','Segoe UI',system-ui,sans-serif";

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const initials = (n) =>
    String(n || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const STATUS_TABS = [
    { value: 'ALL', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'BANK_DONE', label: 'Bank paid' },
    { value: 'PAID', label: 'Paid' },
];

const FilterSelect = ({ id, label, value, onChange, options }) => (
    <div className="pg-fsel">
        <label htmlFor={id}>{label}</label>
        <FormControl size="small">
            <Select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                MenuProps={{ PaperProps: { sx: { borderRadius: '12px', mt: 0.5, fontFamily: FONT } } }}
                sx={{
                    height: 36, minWidth: 130, borderRadius: '10px', fontSize: 14, fontFamily: FONT,
                    color: '#1c1f3d', background: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e8eaf5' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b9bffb' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5146e5', borderWidth: '1px' },
                }}
            >
                {options.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 14, fontFamily: FONT }}>{o.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    </div>
);

// Counts the amount up from 0 whenever the value changes
const AnimatedAmount = ({ value }) => {
    const [shown, setShown] = useState(0);
    useEffect(() => {
        const target = Number(value) || 0;
        const start = performance.now();
        const dur = 900;
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            setShown(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [value]);
    return <>{money(shown)}</>;
};

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(String(text));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) { /* clipboard blocked — ignore */ }
    };
    return (
        <button type="button" className="pg-copy" onClick={copy}>
            <IconCopy size={14} /> {copied ? 'Copied' : 'Copy'}
        </button>
    );
};

const PaymentRow = React.memo(function PaymentRow({
    item, checked, onUndo, narration, onMakePayment, onOpenDoc,
    paymentMode, onPaymentModeChange, onNarrationChange, onManualToggle,
}) {
    const alreadyPaid = item.status === 'P';
    const bankReady = checked || alreadyPaid;
    const missingBeneFields = getMissingBeneFields(item);
    const hasBankDetails = missingBeneFields.length === 0;

    // Bank Make-Payment flow only applies to JK Commodities; every other
    // group keeps the old manual Payment Mode + UTR + checkbox flow.
    const isBankEligible = getBankConfig(item)?.label === 'JK Commodities';
    const manualReady = !!paymentMode && !!String(narration || '').trim();

    return (
        <TableRow>
            <TableCell>
                <div className="pg-strong">{item.Doc_Date}</div>
                <div
                    className="pg-muted"
                    onClick={() => onOpenDoc(item.Doc_No)}
                    title="Open this entry"
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                    Doc No {item.Doc_No}
                </div>
            </TableCell>
            <TableCell>
                <div className="pg-sup">
                    <span className="pg-av">{initials(item.SupplierName)}</span>
                    <span className="pg-supname">{item.SupplierName}</span>
                </div>
            </TableCell>
            <TableCell>{item.System_Name_E}</TableCell>
            <TableCell><span className="pg-pill pg-pill-info">{tranTypeLabel(item.tran_type)}</span></TableCell>
            <TableCell><div className="pg-clamp" title={item.OPnarration}>{item.OPnarration}</div></TableCell>
            <TableCell>{item.Value_date}</TableCell>
            <TableCell align="right" className="pg-num">{formatReadableAmount(item.Bill_Amount)}</TableCell>
            <TableCell align="right" className="pg-num">{formatReadableAmount(item.amount)}</TableCell>
            <TableCell>{item.remark}</TableCell>

            {isBankEligible ? (
                <>
                    <TableCell sx={{ minWidth: 190 }}>
                        {hasBankDetails ? (
                            <div className="pg-bank">
                                <div className="pg-strong">{item.Bank_Name}</div>
                                <div className="pg-muted">A/c {item.Bank_Ac_No}</div>
                                <div className="pg-muted">IFSC {item.IFSC}</div>
                            </div>
                        ) : (
                            <span className="pg-pill pg-pill-bad" title={`Missing: ${missingBeneFields.join(', ')}`}><i />Bank details missing</span>
                        )}
                    </TableCell>
                    <TableCell align="center">
                        {alreadyPaid ? (
                            <span className="pg-pill pg-pill-ok"><i />Paid</span>
                        ) : bankReady ? (
                            <button type="button" className="pg-pill pg-pill-ok pg-pill-btn" onClick={() => onUndo(item.id)} title="Click to undo and redo the payment">
                                <i />UTR {narration || '—'}
                            </button>
                        ) : (
                            <span className="pg-pill pg-pill-warn"><i />Pending</span>
                        )}
                    </TableCell>
                    <TableCell align="center">
                        {alreadyPaid ? (
                            <span className="pg-muted">—</span>
                        ) : bankReady ? (
                            <button type="button" className="pg-rowbtn pg-rowbtn-outline" onClick={() => onMakePayment(item)}>Redo</button>
                        ) : (
                            <button type="button" className="pg-rowbtn pg-rowbtn-primary" onClick={() => onMakePayment(item)}>
                                Pay now <IconArrow size={14} />
                            </button>
                        )}
                    </TableCell>
                </>
            ) : (
                <>
                    <TableCell sx={{ minWidth: '150px' }}>
                        <select
                            className="form-select form-select-sm"
                            value={paymentMode}
                            disabled={alreadyPaid}
                            onChange={(e) => onPaymentModeChange(item.id, e.target.value)}
                            style={{ width: '130px', boxSizing: 'border-box' }}
                        >
                            <option value="">Select</option>
                            {PAYMENT_MODES.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </TableCell>
                    <TableCell sx={{ minWidth: '240px' }}>
                        <textarea
                            className="form-control form-control-sm"
                            value={narration}
                            rows={2}
                            disabled={alreadyPaid}
                            placeholder="UTR / Reference Number"
                            onChange={(e) => onNarrationChange(item.id, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                            style={{ width: '100%', minWidth: '220px', boxSizing: 'border-box', resize: 'none', lineHeight: '1.3' }}
                        />
                    </TableCell>
                    <TableCell align="center">
                        {alreadyPaid ? (
                            <span className="pg-pill pg-pill-ok"><i />Paid</span>
                        ) : (
                            <input
                                type="checkbox"
                                checked={checked}
                                disabled={!manualReady}
                                onChange={() => onManualToggle(item.id)}
                                title={!manualReady ? 'Select Payment Mode and enter UTR / Reference Number first' : ''}
                                style={{ width: '22px', height: '22px', cursor: manualReady ? 'pointer' : 'not-allowed' }}
                            />
                        )}
                    </TableCell>
                </>
            )}
        </TableRow>
    );
});

/* ───────────────────────── page ───────────────────────── */

const OtherPurchasePaymentPaid = () => {
    const companyCode = sessionStorage.getItem('Company_Code');
    const Year_Code = sessionStorage.getItem('Year_Code');
    const Company_Name = sessionStorage.getItem('Company_Name');
    const username = sessionStorage.getItem('username');
    const User_Id = sessionStorage.getItem('User_ID');

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'Doc_No', direction: 'desc' });
    const [checkedIds, setCheckedIds] = useState(new Set());
    const [narrations, setNarrations] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [paymentModes, setPaymentModes] = useState({});
    const [loaderText, setLoaderText] = useState('Saving...');
    const { permissions, canView } = useProgramPermissions('/otherpurchasepaymentpaid_Utility');
    const navigate = useNavigate();

    // Filters
    const [valueDateFilter, setValueDateFilter] = useState('ALL');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [groupFilter, setGroupFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const API_URL = `${apikey}/getall-OtherPurchasePaymentPaid`;
    const MARK_PAID_URL = `${apikey}/other-purchase-payment/mark-paid`;
    const TRANSFER_PAYMENT_URL = `${apikey}/transfer-payment`;
    const GET_STATUS_URL = `${apikey}/get-status`;

    // Make Payment popup state
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [payItem, setPayItem] = useState(null);
    const [payStep, setPayStep] = useState('review'); // review | paying | checking | done | pending | failed
    const [payResult, setPayResult] = useState(null);
    const [payError, setPayError] = useState('');
    const [payMode, setPayMode] = useState('N'); // which PAYMENT_MODES.value to send this transfer as
    const [beneAcType, setBeneAcType] = useState('11'); // which BENE_AC_TYPES.value to send this transfer as

    const abortControllerRef = useRef(null);

    const fetchReportData = useCallback(async () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        try {
            const res = await axios.get(API_URL, {
                params: { Company_Code: companyCode, Year_Code: Year_Code },
                signal: controller.signal,
            });
            setReportData(Array.isArray(res.data?.all_data) ? res.data.all_data : []);
            setError('');
        } catch (err) {
            if (!axios.isCancel(err)) setError('Error fetching report');
        } finally {
            setLoading(false);
        }
    }, [API_URL, companyCode, Year_Code]);

    // Single fetch effect (gated by view permission)
    useEffect(() => {
        if (canView && companyCode && Year_Code) fetchReportData();
        return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
    }, [canView, companyCode, Year_Code, fetchReportData]);

    // Filter option lists built from the data
    const groupOptions = useMemo(() => {
        const names = [...new Set(reportData.map((r) => r.System_Name_E).filter(Boolean))].sort();
        return [{ value: 'ALL', label: 'All groups' }, ...names.map((n) => ({ value: n, label: n }))];
    }, [reportData]);

    const typeOptions = useMemo(() => {
        const codes = [...new Set(reportData.map((r) => String(r.tran_type || '').trim().toUpperCase()).filter(Boolean))];
        return [{ value: 'ALL', label: 'All types' }, ...codes.map((c) => ({ value: c, label: tranTypeLabel(c) }))];
    }, [reportData]);

    const statusOf = useCallback(
        (item) => (item.status === 'P' ? 'PAID' : checkedIds.has(item.id) ? 'BANK_DONE' : 'PENDING'),
        [checkedIds]
    );

    // Everything except the status tab
    const baseItems = useMemo(() => {
        let items = [...reportData];

        if (valueDateFilter === 'TODAY') {
            const today = getLocalDate();
            items = items.filter((item) => normDate(item.Value_date) === today);
        }
        if (typeFilter !== 'ALL') {
            items = items.filter((item) => String(item.tran_type || '').trim().toUpperCase() === typeFilter);
        }
        if (groupFilter !== 'ALL') {
            items = items.filter((item) => item.System_Name_E === groupFilter);
        }
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            items = items.filter((item) =>
                Object.values(item).some((val) => String(val).toLowerCase().includes(q))
            );
        }
        return items;
    }, [reportData, valueDateFilter, typeFilter, groupFilter, searchTerm]);

    // Tab counts follow the other filters
    const statusCounts = useMemo(() => {
        const c = { ALL: baseItems.length, PENDING: 0, BANK_DONE: 0, PAID: 0 };
        baseItems.forEach((i) => { c[statusOf(i)] += 1; });
        return c;
    }, [baseItems, statusOf]);

    const filteredAndSortedData = useMemo(() => {
        let items = statusFilter === 'ALL' ? [...baseItems] : baseItems.filter((i) => statusOf(i) === statusFilter);

        if (sortConfig.key) {
            const dir = sortConfig.direction === 'asc' ? 1 : -1;
            const isDateKey = sortConfig.key === 'Doc_Date' || sortConfig.key === 'Value_date';
            items.sort((a, b) => {
                let ra = a[sortConfig.key] ?? '';
                let rb = b[sortConfig.key] ?? '';
                if (isDateKey) { ra = normDate(ra); rb = normDate(rb); }
                if (!isDateKey && ra !== '' && rb !== '' && !isNaN(ra) && !isNaN(rb)) {
                    return (Number(ra) - Number(rb)) * dir;
                }
                const sa = String(ra).toLowerCase();
                const sb = String(rb).toLowerCase();
                if (sa < sb) return -1 * dir;
                if (sa > sb) return 1 * dir;
                return 0;
            });
        }
        return items;
    }, [baseItems, statusFilter, statusOf, sortConfig]);

    // Back to page 1 whenever the result set changes shape
    useEffect(() => { setPage(0); }, [searchTerm, valueDateFilter, typeFilter, groupFilter, statusFilter, rowsPerPage]);

    const pagedData = useMemo(
        () => filteredAndSortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredAndSortedData, page, rowsPerPage]
    );

    const grandTotal = useMemo(
        () => filteredAndSortedData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
        [filteredAndSortedData]
    );
    const billTotal = useMemo(
        () => filteredAndSortedData.reduce((sum, item) => sum + (parseFloat(item.Bill_Amount) || 0), 0),
        [filteredAndSortedData]
    );

    // Total of the checked rows (already-paid rows are excluded, same as the submit)
    const selectedTotal = useMemo(() => {
        return reportData.reduce((sum, r) => {
            if (checkedIds.has(r.id) && r.status !== 'P') return sum + (parseFloat(r.amount) || 0);
            return sum;
        }, 0);
    }, [reportData, checkedIds]);

    const canSubmit = checkedIds.size > 0;

    // Removable chips for every active filter except the status tab
    const activeChips = [
        searchTerm && { key: 'q', label: `Search: ${searchTerm}`, clear: () => setSearchTerm('') },
        valueDateFilter !== 'ALL' && { key: 'd', label: 'Value date: Today', clear: () => setValueDateFilter('ALL') },
        typeFilter !== 'ALL' && { key: 't', label: `Type: ${tranTypeLabel(typeFilter)}`, clear: () => setTypeFilter('ALL') },
        groupFilter !== 'ALL' && { key: 'g', label: `Group: ${groupFilter}`, clear: () => setGroupFilter('ALL') },
    ].filter(Boolean);

    const filtersActive = activeChips.length > 0 || statusFilter !== 'ALL';

    const clearFilters = () => {
        setSearchTerm('');
        setValueDateFilter('ALL');
        setTypeFilter('ALL');
        setGroupFilter('ALL');
        setStatusFilter('ALL');
    };

    // Opens the underlying Other Purchase entry in a new tab (same convention as GledgerReport's row-click)
    const openOtherPurchaseEntry = (docNo) => {
        if (!docNo) return;
        window.open(`${window.location.origin}/other-purchase?navigatedRecord=${docNo}`, '_blank');
    };

    // New column -> asc, same column -> toggle
    const handleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [
            [Company_Name?.toUpperCase()],
            ['Other Purchase Pay Confirmation'],
            [],
            PRINT_COLUMNS.map((c) => c.label),
        ];
        filteredAndSortedData.forEach((item) => {
            wsData.push(PRINT_COLUMNS.map((col) => (col.key === 'tran_type' ? tranTypeLabel(item.tran_type) : item[col.key])));
        });
        wsData.push([]);
        wsData.push(['', '', '', '', '', '', 'GRAND TOTAL', billTotal, grandTotal]);
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'PaidPayments');
        XLSX.writeFile(wb, `OtherPurchasePaymentsPaid.xlsx`);
    };

    // Undo a completed bank payment for one row, before "Mark as Paid" is submitted
    const handleUndoPayment = useCallback((id) => {
        setCheckedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        setNarrations((prev) => { const { [id]: _drop, ...rest } = prev; return rest; });
        setPaymentModes((prev) => { const { [id]: _drop, ...rest } = prev; return rest; });
    }, []);

    // --- Old manual flow (non-JK Commodities groups): Payment Mode select +
    // UTR/Reference textarea + a plain "Paid" checkbox, same as before the
    // bank-API redesign. No bank call involved.
    const NARRATION_MAX = 100;

    const handleNarrationChange = useCallback((id, value) => {
        value = value.replace(/\s*[\r\n]+\s*/g, ' ');
        if (value.length > NARRATION_MAX) {
            Swal.fire({
                icon: 'warning',
                title: 'Narration too long',
                text: `Narration cannot be more than ${NARRATION_MAX} characters (you entered ${value.length}).`,
            });
            value = value.slice(0, NARRATION_MAX);
        }
        setNarrations((prev) => ({ ...prev, [id]: value }));
    }, []);

    const handlePaymentModeChange = useCallback((id, value) => {
        setPaymentModes((prev) => ({ ...prev, [id]: value }));
    }, []);

    const handleManualToggle = useCallback((id) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const openPayDialog = useCallback((item) => {
        setPayItem(item);
        setPayStep('review');
        setPayResult(null);
        setPayError('');
        setPayMode('N');
        setBeneAcType('11');
        setPayDialogOpen(true);
    }, []);

    // payItem is kept so the popup content stays visible while it animates out
    const closePayDialog = () => {
        setPayDialogOpen(false);
    };

    const buildTransferPayload = (item, mode, acType) => ({
        TransferPaymentRequest: {
            SubHeader: { serviceRequestId: 'OpenAPI', serviceRequestVersion: '1.0', channelId: '' },
            TransferPaymentRequestBody: {
                paymentDetails: [{
                    txnPaymode: PAYMENT_MODES.find((m) => m.value === mode)?.txnPaymode || 'PA',
                    valueDate: getLocalDate(), // bank requires today's execution date, not the bill's stored due date
                    txnAmount: String(parseFloat(item.amount) || 0),
                    beneLEI: '',
                    beneName: item.Benificiary_Name || item.SupplierName,
                    beneCode: String(item.SupplierAcCode || ''), // backend prefixes this with the corp code (e.g. JKEBUY13383)
                    beneAccNum: item.Bank_Ac_No,
                    beneAcType: acType || '11',
                    beneAddr1: item.Address_E || '',
                    beneAddr2: '',
                    beneAddr3: '',
                    beneCity: item.cityname || '',
                    beneState: item.State_Name || '',
                    benePincode: item.Pincode || '',
                    beneIfscCode: item.IFSC,
                    beneBankName: item.Bank_Name,
                    baseCode: '',
                    chequeNumber: '',
                    chequeDate: '',
                    payableLocation: '',
                    printLocation: '',
                    beneEmailAddr1: item.Email_Id || '',
                    beneMobileNo: String(item.Mobile_No || '').replace(/\D/g, ''),
                    productCode: '',
                    txnType: 'VEND',
                    invoiceDetails: [{
                        invoiceAmount: String(parseFloat(item.Bill_Amount) || 0),
                        invoiceNumber: String(item.Doc_No || ''),
                        invoiceDate: toISODate(item.Doc_Date),
                        cashDiscount: '0.00',
                        tax: '0.00',
                        netAmount: '0.00',
                        invoiceInfo1: '', invoiceInfo2: '', invoiceInfo3: '', invoiceInfo4: '', invoiceInfo5: '',
                    }],
                    enrichment1: '', enrichment2: '', enrichment3: '', enrichment4: '', enrichment5: '',
                    senderToReceiverInfo: `Payment to ${item.SupplierName} against Doc No ${item.Doc_No} dated ${item.Doc_Date}, Bill Amount Rs.${money(item.Bill_Amount)}`,
                }],
            },
        },
    });

    // Marks the row paid-via-bank locally: fills UTR + payment mode and checks it,
    // ready for the existing "Mark as Paid" bulk submit to create the BP voucher.
    const finalizeSuccessfulPayment = (item, txn, mode) => {
        setNarrations((prev) => ({ ...prev, [item.id]: txn.utrNo || txn.crn || '' }));
        setPaymentModes((prev) => ({ ...prev, [item.id]: mode }));
        setCheckedIds((prev) => { const next = new Set(prev); next.add(item.id); return next; });
    };

    const checkPaymentStatus = async (crn) => {
        setPayStep('checking');
        try {
            const { data } = await axios.post(GET_STATUS_URL, {
                GetStatusRequest: { SubHeader: {}, GetStatusRequestBody: { crn: [crn] } },
            });
            const body = data?.GetStatusResponse?.GetStatusResponseBody;
            const txn = body?.data?.CUR_TXN_ENQ?.[0];

            if (!txn) {
                setPayStep('failed');
                setPayError(body?.data?.errorMessage || body?.message || 'Could not fetch payment status from the bank.');
                return;
            }

            setPayResult({ ...txn, custUniqRef: crn });

            if (txn.transactionStatus === 'PROCESSED') {
                finalizeSuccessfulPayment(payItem, txn, payMode);
                setPayStep('done');
            } else if (txn.transactionStatus === 'PENDING') {
                setPayStep('pending');
            } else {
                setPayStep('failed');
                setPayError(txn.statusDescription || 'Payment was not successful.');
            }
        } catch (err) {
            setPayStep('failed');
            setPayError(err.response?.data?.message || err.message || 'Status check failed.');
        }
    };

    const handlePay = async () => {
        if (!payItem) return;
        setPayStep('paying');
        setPayError('');
        try {
            const { data } = await axios.post(TRANSFER_PAYMENT_URL, buildTransferPayload(payItem, payMode, beneAcType));
            const crn = data?.custUniqRef;
            const body = data?.TransferPaymentResponse?.TransferPaymentResponse;

            if (!crn) {
                setPayStep('failed');
                setPayError(body?.message || 'The bank did not accept this transfer.');
                return;
            }
            await checkPaymentStatus(crn);
        } catch (err) {
            setPayStep('failed');
            setPayError(
                err.response?.data?.TransferPaymentResponse?.TransferPaymentResponse?.message ||
                err.message || 'Transfer failed.'
            );
        }
    };

    const handlePrintPdf = () => {
        if (filteredAndSortedData.length === 0) {
            Swal.fire({ icon: 'info', title: 'No data', text: 'There are no records to print.' });
            return;
        }

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text((Company_Name || '').toUpperCase(), pageWidth / 2, 12, { align: 'center' });
        doc.setFontSize(11);
        doc.text('Other Purchase Pay', pageWidth / 2, 18, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`Printed on: ${new Date().toLocaleString('en-IN')}`, pageWidth - 14, 18, { align: 'right' });

        const body = filteredAndSortedData.map((item) => PRINT_COLUMNS.map((col) => {
            if (col.key === 'tran_type') return tranTypeLabel(item.tran_type);
            if (col.numeric) return formatReadableAmount(item[col.key]);
            return item[col.key] ?? '';
        }));

        const billIdx = PRINT_COLUMNS.findIndex((c) => c.key === 'Bill_Amount');
        const columnStyles = {};
        PRINT_COLUMNS.forEach((c, i) => { if (c.numeric) columnStyles[i] = { halign: 'right' }; });

        autoTable(doc, {
            startY: 22,
            head: [PRINT_COLUMNS.map((c) => c.label)],
            body,
            foot: [[
                { content: 'GRAND TOTAL', colSpan: billIdx, styles: { halign: 'right' } },
                { content: formatReadableAmount(billTotal), styles: { halign: 'right' } },
                { content: formatReadableAmount(grandTotal), styles: { halign: 'right' } },
                '',
            ]],
            showFoot: 'lastPage',
            theme: 'grid',
            margin: { left: 14, right: 14, bottom: 14 },
            styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak', valign: 'middle' },
            headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: [238, 240, 255], textColor: 0, fontStyle: 'bold' },
            columnStyles,
        });

        const totalPages = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
        }

        window.open(doc.output('bloburl'), '_blank');
    };

    // Typed value wins; otherwise fall back to what the API returned
    const getNarration = (row) =>
        String(narrations[row.id] ?? row.narration ?? '').trim().slice(0, 100);

    // New detail line for one other-purchase payment
    const buildNewDetail = (item, detailId) => ({
        rowaction: 'add',
        detail_id: detailId,
        credit_ac: item.Supplier_Code,
        ca: item.sc,
        Unit_Code: 0,
        uc: 0,
        Group_Code: Number(item.Group_Code),
        gcid: Number(item.gcid),
        AcadjAccode: 0, ac: 0,
        TDSAc: 0, TDSAcid: 0,
        Voucher_No: 0,
        Voucher_Type: '',
        Tender_No: 0,
        tenderdetailid: 0,
        amount: parseFloat(item.amount) || 0,
        Adjusted_Amount: 0,
        AcadjAmt: 0,
        narration: getNarration(item),
        narration2: '',
        drpFilterValue: 'O',
        TDS_Rate: 0,
        TDS_Amt: 0,
        GRN: '',
        TReceipt: '',
        Company_Code: companyCode,
        Year_Code: Year_Code,
    });

    // Existing detail line sent back unchanged
    const existingToPayload = (d) => ({
        rowaction: 'Normal',
        detail_id: d.detail_id,
        credit_ac: d.credit_ac,
        Unit_Code: d.Unit_Code,
        Voucher_No: d.Voucher_No,
        Voucher_Type: d.Voucher_Type,
        Tender_No: d.Tender_No,
        tenderdetailid: d.tenderdetailid,
        amount: d.amount,
        Adjusted_Amount: d.Adjusted_Amount,
        narration: d.narration,
        narration2: d.narration2,
        drpFilterValue: d.drpFilterValue,
        trandetailid: d.trandetailid,
        AcadjAmt: d.AcadjAmt || 0,
        Group_Code: d.Group_Code,
        AcadjAccode: d.AcadjAccode,
        ca: d.ca, uc: d.uc, ac: d.ac, gcid: d.gcid,
        TDS_Rate: d.TDS_Rate || 0,
        TDS_Amt: d.TDS_Amt || 0,
        GRN: d.GRN,
        TReceipt: d.TReceipt,
        Company_Code: companyCode,
        Year_Code: Year_Code,
        TDSAc: d.TDSAc,
        TDSAcid: d.TDSAcid,
    });

    const findTodaysVoucher = async (docDate, cashbankCode) => {
        let last = null;
        try {
            const res = await axios.get(`${apikey}/get-lastreceiptpayment-navigation`, {
                params: { Company_Code: companyCode, Year_Code: Year_Code, tran_type: RECEIPT_TRAN_TYPE },
            });
            last = res.data?.last_head_data;
        } catch (err) {
            if (err.response?.status !== 404) throw err;
        }
        if (!last) return null;

        const isToday = String(last.doc_date || '').slice(0, 10) === docDate;
        const sameBank = Number(last.cashbank) === cashbankCode;
        if (!isToday || !sameBank) return null;

        const { data } = await axios.get(`${apikey}/getreceiptpaymentByid`, {
            params: { Company_Code: companyCode, Year_Code: Year_Code, tran_type: RECEIPT_TRAN_TYPE, doc_no: last.doc_no },
        });
        const head = data.receipt_payment_head;
        if (head.LockedRecord) {
            throw new Error(`Today's voucher (BP ${head.doc_no}) is locked by ${head.LockedUser}. Try again once they close it.`);
        }
        return { head, details: Array.isArray(data.receipt_payment_details) ? data.receipt_payment_details : [] };
    };

    const saveToTodaysVoucher = async (items, docDate, bankConfig) => {
        const existing = await findTodaysVoucher(docDate, bankConfig.cashbank);
        const sum = (rows) => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

        if (existing) {
            const { head, details } = existing;
            let nextId = details.reduce((m, d) => Math.max(m, Number(d.detail_id) || 0), 0) + 1;
            const newDetails = items.map((item) => buildNewDetail(item, nextId++));

            const head_data = {
                ...head,
                total: sum([...details, ...newDetails]).toFixed(2),
                Modified_By: username,
                User_Id: User_Id,
                tranid: undefined,
            };
            const detail_data = [...details.map(existingToPayload), ...newDetails];

            await axios.put(`${apikey}/update-receiptpayment`, { head_data, detail_data }, {
                params: { tranid: head.tranid },
            });
            return { docNo: head.doc_no, updated: true };
        }

        const { data } = await axios.get(`${apikey}/get_next_paymentRecord_docNo`, {
            params: { Company_Code: companyCode, tran_type: RECEIPT_TRAN_TYPE, Year_Code: Year_Code },
        });
        const docNo = data.next_doc_no;

        let nextId = 1;
        const newDetails = items.map((item) => buildNewDetail(item, nextId++));
        const head_data = {
            tran_type: RECEIPT_TRAN_TYPE,
            doc_no: docNo,
            doc_date: docDate,
            cashbank: bankConfig.cashbank,
            cb: bankConfig.cb,
            total: sum(newDetails).toFixed(2),
            company_code: companyCode,
            year_code: Year_Code,
            Created_By: username,
            Modified_By: '',
        };
        await axios.post(`${apikey}/insert-receiptpayment`, { head_data, detail_data: newDetails });
        return { docNo, updated: false };
    };

    const paymentModeLabel = (code) =>
        PAYMENT_MODES.find((m) => m.value === code)?.label || '';

    const buildNotification = (item) => {
        const tpl = messageTemplates.vendor_payment;
        const data = {
            SupplierName: item.SupplierName,
            billno: item.Doc_No,
            Doc_DateConverted: item.Doc_Date,
            Bill_Amount: money(item.Bill_Amount),
            tds_amount: money(item.TDS),
            net_amount: money(item.amount),
            Value_date: item.Value_date,
            payment_mode: paymentModeLabel(paymentModes[item.id]),
            narration: getNarration(item),
            Company_Name_E: Company_Name,
        };
        return {
            payment_id: item.id,
            supplier: item.SupplierName,
            mobile: cleanMobile(item.whatsup_no),
            email: String(item.Email_Id || '').trim(),
            template_name: tpl.template_name,
            language: tpl.language,
            params: tpl.params.map((p) => fillTemplate(p, data)),
            subject: fillTemplate(tpl.subject, data),
            body: fillTemplate(tpl.body, data),
        };
    };

    // Never throws: a notification problem must not affect the saved payments.
    const sendVendorNotifications = async (rows) => {
        const notifications = rows.map(buildNotification).filter((n) => n.mobile || n.email);
        const noContact = rows.length - notifications.length;
        if (notifications.length === 0) {
            return { text: ' No WhatsApp number or email found for these vendors.', failed: true };
        }
        try {
            const { data } = await axios.post(`${apikey}/other-purchase-payment/notify`, { notifications });
            const results = Array.isArray(data?.results) ? data.results : [];
            const countText = (ch) => results.filter((r) => r[ch] === 'sent').length;
            const skipped = (ch) => results.filter((r) => r[ch] === 'skipped').length;
            const bad = results.filter((r) => r.whatsapp?.startsWith('failed') || r.email?.startsWith('failed'));
            let text = ` WhatsApp sent: ${countText('whatsapp')}, Email sent: ${countText('email')}.`;
            if (skipped('email')) text += ` ${skipped('email')} vendor(s) have no email.`;
            if (noContact) text += ` No contact details for ${noContact} vendor(s).`;
            if (bad.length) text += ` Failed for: ${[...new Set(bad.map((r) => r.supplier))].join(', ')}.`;
            return { text, failed: bad.length > 0 || noContact > 0 };
        } catch (e) {
            return { text: ' Payments saved, but notifications could not be sent.', failed: true };
        }
    };

    const handleMarkPaidSubmit = async () => {
        if (checkedIds.size === 0) {
            alert('Please check at least one record to mark as paid');
            return;
        }

        const docDate = getLocalDate();
        if (await PostDateRecordLock(docDate, sessionStorage.getItem('Post_Date'))) return;
        if (!validateDocumentDate(docDate, sessionStorage.getItem('Accounting_Year'))) return;

        const selected = reportData.filter((r) => checkedIds.has(r.id) && r.status !== 'P');

        // Payment Mode and UTR / Reference Number are compulsory for every checked row
        const missingMode = selected.filter((r) => !paymentModes[r.id]);
        const missingUtr = selected.filter((r) => !getNarration(r));

        if (missingMode.length > 0 || missingUtr.length > 0) {
            const lines = [];
            if (missingMode.length) {
                lines.push(`Payment Mode missing for Doc No: ${missingMode.map((r) => r.Doc_No).join(', ')}`);
            }
            if (missingUtr.length) {
                lines.push(`UTR / Reference Number missing for Doc No: ${missingUtr.map((r) => r.Doc_No).join(', ')}`);
            }
            Swal.fire({ icon: 'error', title: 'Required fields missing', html: lines.join('<br/>') });
            return;
        }

        // AD rows: no BP voucher is created, they are only marked as paid
        const adRows = selected.filter((r) => r.tran_type === 'AD');
        const voucherRows = selected.filter((r) => r.tran_type !== 'AD');

        const skippedNoSupplier = voucherRows.filter((r) => !(r.Supplier_Code && r.sc));
        const withSupplier = voucherRows.filter((r) => r.Supplier_Code && r.sc);

        const skippedNoGroup = withSupplier.filter((r) => !getBankConfig(r));
        const valid = withSupplier.filter((r) => getBankConfig(r));

        // if (valid.length === 0 && adRows.length === 0) {
        //     Swal.fire({
        //         icon: 'warning',
        //         title: 'Nothing to save',
        //         text: 'The selected rows have no supplier account or a recognized group.',
        //     });
        //     return;
        // }

        // Group the valid rows by Group_Code so each group posts to its own bank/voucher
        const groups = valid.reduce((acc, item) => {
            const gc = Number(item.Group_Code);
            (acc[gc] = acc[gc] || []).push(item);
            return acc;
        }, {});

        setSubmitting(true);
        setLoaderText('Saving payment vouchers...');

        const results = [];
        const failed = [];
        const savedIds = [];

        // Sequential: each voucher needs its own next doc_no, and a group's "today" voucher
        // must be looked up fresh after any earlier group's insert/update.
        for (const [groupCode, items] of Object.entries(groups)) {
            const bankConfig = BANK_BY_GROUP[groupCode];
            try {
                const result = await saveToTodaysVoucher(items, docDate, bankConfig);
                results.push({ groupCode, label: bankConfig.label, ...result, count: items.length });
                savedIds.push(...items.map((i) => i.id));
            } catch (err) {
                failed.push({
                    groupCode,
                    label: bankConfig.label,
                    error: err.response?.data?.error || err.response?.data?.message || err.message,
                });
            }
        }

        if (savedIds.length === 0 && adRows.length === 0) {
            setSubmitting(false);
            Swal.fire({ icon: 'error', title: 'Payment voucher(s) not saved', text: failed.map((f) => `${f.label}: ${f.error}`).join(' | ') });
            return;
        }
        const savedRows = [...valid.filter((r) => savedIds.includes(r.id)), ...adRows];

        try {
            await axios.post(MARK_PAID_URL, {
                payments: savedRows.map((r) => ({
                    id: r.id,
                    narration: getNarration(r),
                    payment_mode: paymentModes[r.id] || '',
                })),
            });
        } catch (err) {
            setSubmitting(false);
            setCheckedIds(new Set());
            await fetchReportData();
            Swal.fire({
                icon: 'warning',
                title: 'Voucher(s) saved, status not updated',
                text: `Voucher(s) ${results.map((r) => r.docNo).join(', ')} were saved, but marking the payments as paid failed. Do not submit them again, or they will be added twice.`,
            });
            return;
        }
        setLoaderText('Sending WhatsApp and email to vendors...');
        const notify = await sendVendorNotifications(savedRows);

        setLoaderText('Refreshing list...');
        setSubmitting(false);
        setCheckedIds(new Set());
        setNarrations({});
        setPaymentModes({});

        await fetchReportData();

        const adText = adRows.length
            ? `${adRows.length} advance (AD) payment(s) marked paid without a voucher: Doc ${adRows.map((r) => r.Doc_No).join(', ')}`
            : '';

        const successText = [
            results.map((r) => `${r.label}: ${r.count > 1 ? 'multiple' : r.count} payment(s) ${r.updated ? 'added to' : 'created in'} BP voucher ${r.docNo}`).join('. '),
            adText,
        ].filter(Boolean).join('. ');

        const skipText = [
            skippedNoSupplier.length ? `no supplier: Doc ${skippedNoSupplier.map((r) => r.Doc_No).join(', ')}` : null,
            skippedNoGroup.length ? `unrecognized group: Doc ${skippedNoGroup.map((r) => r.Doc_No).join(', ')}` : null,
            failed.length ? `failed: ${failed.map((f) => `${f.label} (${f.error})`).join('; ')}` : null,
        ].filter(Boolean).join('. ');

        Swal.fire({
            icon: (skippedNoSupplier.length || skippedNoGroup.length || failed.length || notify.failed) ? 'warning' : 'success',
            title: 'Done',
            text: successText + (skipText ? `. Skipped/failed — ${skipText}.` : '') + notify.text,
        });
    };

    const handleValueDateFilter = (mode) => {
        setValueDateFilter(mode);
        setCheckedIds(new Set()); // avoid submitting rows that are now hidden
    };

    // Filters that hide rows must also drop hidden selections
    const handleHidingFilter = (setter) => (val) => {
        setter(val);
        setCheckedIds(new Set());
    };

    if (canView === null) return null;
    if (canView === false) return <PageNotFound />;

    /* ── dialog derived values ── */
    const busy = payStep === 'paying' || payStep === 'checking';
    const missing = payItem ? getMissingBeneFields(payItem) : [];
    const progress = { review: 10, paying: 45, checking: 78, done: 100, pending: 78, failed: 78 }[payStep] || 10;
    const progClass = payStep === 'done' ? 'pg-prog-ok' : payStep === 'pending' ? 'pg-prog-warn' : payStep === 'failed' ? 'pg-prog-bad' : '';
    const flowClass = busy ? 'pg-flow-go' : payStep === 'done' ? 'pg-flow-done' : payStep === 'failed' ? 'pg-flow-off' : '';
    const stepOn = (i) => (payStep === 'review' ? i === 0 : payStep === 'paying' ? i === 1 : i === 2);

    const payRows = payItem ? [
        ['Supplier', payItem.SupplierName],
        ['Group', payItem.System_Name_E],
        ['Doc no', payItem.Doc_No],
        ['Doc date', payItem.Doc_Date],
        ['Narration', payItem.OPnarration],
        ['Due date', payItem.Value_date],
        ['Transfer date', `Today · ${getLocalDate().split('-').reverse().join('/')}`],
        ['Mode', PAYMENT_MODES.find((m) => m.value === payMode)?.label || 'NEFT'],
        ['Beneficiary A/c type', BENE_AC_TYPES.find((t) => t.value === beneAcType)?.label || ''],
        ['Bill amount', `₹ ${money(payItem.Bill_Amount)}`],
        (parseFloat(payItem.TDS) || 0) > 0 ? ['TDS', `₹ ${money(payItem.TDS)}`] : null,
        ['Mobile', (() => { const d = String(payItem.Mobile_No || payItem.whatsup_no || '').replace(/\D/g, ''); return d.length >= 10 ? d : ''; })()],
        ['Email', payItem.Email_Id],
        ['City', [payItem.cityname, payItem.State_Name].filter(Boolean).join(', ')],
    ].filter((r) => r && String(r[1] ?? '').trim() !== '') : [];

    return (
        <Box className="pg-page" sx={{ padding: '16px', marginTop: '10px' }}>
            <style>{PAGE_CSS}</style>

            {/* Header */}
            <div className="pg-head">
                <div className="pg-head-left">
                    {/* <BackButton onClick={() => navigate('/dashboard')} /> */}
                    <div>
                        <h1 className="pg-title">Other Purchase Pay</h1>
                        {/* <p className="pg-sub">Pay vendors through the bank, then mark them paid to post the BP voucher.</p> */}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="pg-btn" onClick={handlePrintPdf}>Print PDF</button>
                    <button className="pg-btn" onClick={handleExportToExcel}>Export Excel</button>
                </div>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            <div className="pg-card">
                {/* Status tabs + search */}
                <div className="pg-toolbar">
                    <div className="pg-tabs" role="tablist">
                        {STATUS_TABS.map((t) => (
                            <button
                                key={t.value}
                                type="button"
                                role="tab"
                                aria-selected={statusFilter === t.value}
                                className={`pg-tab ${statusFilter === t.value ? 'pg-tab-on' : ''}`}
                                onClick={() => setStatusFilter(t.value)}
                            >
                                {t.label}
                                <span className="pg-count">{statusCounts[t.value]}</span>
                            </button>
                        ))}
                    </div>
                    <div className="pg-search">
                        <IconSearch size={16} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search supplier, doc no, narration…"
                            aria-label="Search payments"
                        />
                    </div>
                </div>

                {/* Filter row */}
                <div className="pg-frow">
                    <span className="pg-ftitle"><IconFilter size={15} /> Filters</span>
                    <FilterSelect
                        id="valueDateFilter" label="Value date" value={valueDateFilter}
                        onChange={handleValueDateFilter}
                        options={[{ value: 'ALL', label: 'All dates' }, { value: 'TODAY', label: 'Today' }]}
                    />
                    <FilterSelect
                        id="typeFilter" label="Type" value={typeFilter}
                        onChange={handleHidingFilter(setTypeFilter)} options={typeOptions}
                    />
                    <FilterSelect
                        id="groupFilter" label="Group" value={groupFilter}
                        onChange={handleHidingFilter(setGroupFilter)} options={groupOptions}
                    />
                    <div className="pg-fchips">
                        {activeChips.map((c) => (
                            <span key={c.key} className="pg-fchip">
                                {c.label}
                                <button type="button" onClick={c.clear} aria-label={`Remove ${c.label}`}><IconClose size={11} sw={3} /></button>
                            </span>
                        ))}
                        {filtersActive && (
                            <button type="button" className="pg-btn pg-btn-ghost" onClick={clearFilters}>Clear all</button>
                        )}
                    </div>

<div className="pg-bar" style={{marginBottom: 5}}>
                <div className="pg-stats">
                    <div className="pg-stat"><span>Total bill amount</span><b>{formatReadableAmount(billTotal)}</b></div>
                    <div className="pg-stat"><span>Total pay amount</span><b>{formatReadableAmount(grandTotal)}</b></div>
                    <div className="pg-stat pg-stat-sel">
                        <span>Ready to mark paid ({checkedIds.size})</span>
                        <b>{formatReadableAmount(selectedTotal)}</b>
                    </div>
                </div>
             
            </div>

                    

                    <button
                        type="button"
                        className="pg-btn pg-btn-primary"
                        style={{ marginLeft: 'auto' }}
                        onClick={handleMarkPaidSubmit}
                        disabled={!canSubmit || submitting || permissions.canSave === 'N' || permissions.canView === 'N' || permissions.canEdit === 'N' || permissions.canDelete === 'N'}
                    >
                        <IconCheck size={16} /> {submitting ? 'Saving…' : `Mark ${checkedIds.size || ''} as paid`.replace('  ', ' ')}
                    </button>
                </div>

                <div className="pg-tmeta">
                    Showing {filteredAndSortedData.length === 0 ? 0 : page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} records
                </div>

                {/* Table */}
                <TableContainer sx={{ maxHeight: 620}}>
                    <Table stickyHeader size="small" className="pg-table">
                        <TableHead>
                            <TableRow>
                                {SCREEN_COLUMNS.map((col) => {
                                    const sortable = !col.isStatus && !col.isInput && !col.isInfo;
                                    const isSorted = sortConfig.key === col.key;
                                    const icon = isSorted ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕';
                                    return (
                                        <TableCell
                                            key={col.key}
                                            align={col.numeric ? 'right' : col.isStatus ? 'center' : 'left'}
                                            className={`${sortable ? 'pg-sortable' : ''} ${isSorted ? 'pg-sorted' : ''}`}
                                            onClick={sortable ? () => handleSort(col.key) : undefined}
                                            style={{ width: col.width }}
                                        >
                                            {col.label}
                                            {sortable && <span className="pg-sort">{icon}</span>}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedData.map((item) => (
                                <PaymentRow
                                    key={item.id}
                                    item={item}
                                    checked={checkedIds.has(item.id)}
                                    onUndo={handleUndoPayment}
                                    narration={narrations[item.id] ?? item.narration ?? ''}
                                    onMakePayment={openPayDialog}
                                    onOpenDoc={openOtherPurchaseEntry}
                                    paymentMode={paymentModes[item.id] ?? ''}
                                    onPaymentModeChange={handlePaymentModeChange}
                                    onNarrationChange={handleNarrationChange}
                                    onManualToggle={handleManualToggle}
                                />
                            ))}
                            {!loading && pagedData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={SCREEN_COLUMNS.length} sx={{ border: 0 }}>
                                        <div className="pg-empty">
                                            <IconInbox size={38} />
                                            <b>No payments found</b>
                                            {filtersActive ? 'Try removing a filter or changing the search.' : 'Nothing is waiting for payment right now.'}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 3 }}>
                            <TableRow>
                                <TableCell colSpan={6} align="right">Total ({filteredAndSortedData.length} records)</TableCell>
                                <TableCell align="right" className="pg-num">{formatReadableAmount(billTotal)}</TableCell>
                                <TableCell align="right" className="pg-num">{formatReadableAmount(grandTotal)}</TableCell>
                                <TableCell colSpan={4} />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredAndSortedData.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ borderTop: '1px solid #e8eaf5', fontFamily: FONT }}
                />
            </div>

 <div  style={{marginBottom: 50}}>

                 </div> 

            {/* Action bar */}
            {/* <div className="pg-bar" style={{marginBottom: 50}}>
                <div className="pg-stats">
                    <div className="pg-stat"><span>Total bill amount</span><b>{formatReadableAmount(billTotal)}</b></div>
                    <div className="pg-stat"><span>Total pay amount</span><b>{formatReadableAmount(grandTotal)}</b></div>
                    <div className="pg-stat pg-stat-sel">
                        <span>Ready to mark paid ({checkedIds.size})</span>
                        <b>{formatReadableAmount(selectedTotal)}</b>
                    </div>
                </div>
                <button
                    className="pg-btn pg-btn-primary"
                    onClick={handleMarkPaidSubmit}
                    disabled={!canSubmit || submitting || permissions.canSave === 'N' || permissions.canView === 'N' || permissions.canEdit === 'N' || permissions.canDelete === 'N'}
                >
                    <IconCheck size={16} /> {submitting ? 'Saving…' : `Mark ${checkedIds.size || ''} as paid`.replace('  ', ' ')}
                </button>
            </div> */}

            {/* Loader */}
            {(loading || submitting) && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <ScaleLoader color="#5146e5" size={60} />
                        <Typography sx={{ mt: 2, fontWeight: 600, color: '#5146e5', fontFamily: FONT }}>
                            {submitting ? loaderText : 'Loading data…'}
                        </Typography>
                    </Box>
                </div>
            )}

            {/* ───────── Make Payment popup (single card) ───────── */}
            <Dialog
                open={payDialogOpen}
                onClose={busy ? undefined : closePayDialog}
                maxWidth={false}
                fullWidth
                TransitionComponent={SlideUp}
                transitionDuration={{ enter: 420, exit: 220 }}
                className="pg-dlg"
            >
                {payItem && (
                    <>
                        {/* Header: amount + key facts */}
                        <div className="pg-sh">
                            <span className="pg-blob pg-blob-a" />
                            <span className="pg-blob pg-blob-b" />
                            <button className="pg-x" onClick={closePayDialog} disabled={busy} aria-label="Close"><IconClose size={17} /></button>
                            <div className="pg-rise" style={{ '--d': '40ms' }}>
                                <div className="pg-lbl">Payment summary · You are paying</div>
                                <div className="pg-amt">₹ <AnimatedAmount value={payItem.amount} /></div>
                                <div className="pg-lsub">{payItem.SupplierName}</div>
                                <div className="pg-metas">
                                    <span className="pg-meta">Doc No {payItem.Doc_No}</span>
                                    <span className="pg-meta">{tranTypeLabel(payItem.tran_type)}</span>
                                    <span className="pg-meta">{PAYMENT_MODES.find((m) => m.value === payMode)?.label || 'NEFT'}</span>
                                    <span className="pg-meta">Executes today · {getLocalDate().split('-').reverse().join('/')}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pg-sb">
                            {/* From → To banks */}
                            <div className="pg-rise" style={{ '--d': '120ms' }}>
                                <div className="pg-sec">Transfer</div>
                                <div className="pg-banks">
                                    <div className="pg-bcard">
                                        <div className="pg-bcard-top"><span>From</span><IconBank size={20} /></div>
                                        <div className="pg-bcard-name">{Company_Name}</div>
                                        <div className="pg-bcard-sub">{getBankConfig(payItem)?.label || payItem.System_Name_E} · company account</div>
                                    </div>

                                    <div className={`pg-hflow ${flowClass}`}><span /><span /></div>

                                    <div className={`pg-bcard pg-bcard-to ${missing.length ? 'pg-bcard-warn' : ''}`}>
                                        <div className="pg-bcard-top">
                                            <span>To · Beneficiary bank</span>
                                            <span className="pg-chipimg" />
                                        </div>
                                        {missing.length > 0 ? (
                                            <>
                                                <div className="pg-bcard-name">Bank details incomplete</div>
                                                <div className="pg-bcard-sub">Missing in Account Master: {missing.join(', ')}</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="pg-bcard-name">{payItem.Bank_Name}</div>
                                                <div className="pg-bcard-ac">{groupAccountNo(payItem.Bank_Ac_No)}</div>
                                                <div className="pg-bcard-meta">
                                                    <div><span>IFSC</span><b>{payItem.IFSC}</b></div>
                                                    <div><span>Beneficiary</span><b>{payItem.Benificiary_Name || payItem.SupplierName}</b></div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="pg-paymode" style={{ marginTop: 14 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginRight: 10 }}>Pay via</span>
                                    <div style={{ display: 'inline-flex', gap: 6 }}>
                                        {BANK_PAY_MODES.map((m) => (
                                            <button
                                                key={m.value}
                                                type="button"
                                                disabled={payStep !== 'review'}
                                                onClick={() => setPayMode(m.value)}
                                                style={{
                                                    padding: '5px 14px',
                                                    borderRadius: 999,
                                                    fontSize: 12.5,
                                                    fontWeight: 600,
                                                    cursor: payStep === 'review' ? 'pointer' : 'default',
                                                    border: payMode === m.value ? '1px solid #5146e5' : '1px solid #d8dbe8',
                                                    background: payMode === m.value ? '#5146e5' : '#fff',
                                                    color: payMode === m.value ? '#fff' : '#43485a',
                                                    opacity: payStep !== 'review' && payMode !== m.value ? 0.5 : 1,
                                                }}
                                            >
                                                {m.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="pg-paymode" style={{ marginTop: 10 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginRight: 10 }}>Beneficiary A/c type</span>
                                    <select
                                        value={beneAcType}
                                        disabled={payStep !== 'review'}
                                        onChange={(e) => setBeneAcType(e.target.value)}
                                        style={{
                                            padding: '5px 10px',
                                            borderRadius: 8,
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            color: '#43485a',
                                            border: '1px solid #d8dbe8',
                                            background: '#fff',
                                            cursor: payStep === 'review' ? 'pointer' : 'default',
                                        }}
                                    >
                                        {BENE_AC_TYPES.map((t) => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* All details */}
                            <div className="pg-rise" style={{ '--d': '200ms' }}>
                                <div className="pg-sec">Payment details</div>
                                <div className="pg-grid2">
                                    {payRows.map(([k, v]) => (
                                        <div className="pg-row" key={k}><span>{k}</span><b>{v}</b></div>
                                    ))}
                                    <div className="pg-row pg-row-net"><span>Net payable</span><b>₹ {money(payItem.amount)}</b></div>
                                </div>
                            </div>

                            {/* Progress */}
                            <div className={`pg-rise ${progClass}`} style={{ '--d': '280ms' }}>
                                <div className="pg-prog"><i style={{ width: `${progress}%` }} /></div>
                                <div className="pg-prog-lbl">
                                    {['Review', 'Transfer', 'Confirm'].map((l, i) => (
                                        <span key={l} className={stepOn(i) ? 'on' : ''}>{l}</span>
                                    ))}
                                </div>
                            </div>

                            {/* Result — key replays the entrance on every state change */}
                            {payStep === 'paying' && (
                                <div key="paying" className="pg-res pg-res-busy">
                                    <div className="pg-ico pg-pulse"><CircularProgress size={26} sx={{ color: '#5146e5' }} /></div>
                                    <div><b>Sending transfer to the bank</b><span className="t">Please keep this window open.</span></div>
                                </div>
                            )}
                            {payStep === 'checking' && (
                                <div key="checking" className="pg-res pg-res-busy">
                                    <div className="pg-ico pg-pulse"><CircularProgress size={26} sx={{ color: '#5146e5' }} /></div>
                                    <div><b>Confirming with the bank</b><span className="t">Checking the payment status…</span></div>
                                </div>
                            )}
                            {payStep === 'done' && payResult && (
                                <>
                                    <div key="done" className="pg-res pg-res-ok">
                                        <div className="pg-ico">
                                            <div className="pg-ico-c">
                                                <svg className="pg-tick" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                                            </div>
                                        </div>
                                        <div><b>Payment successful</b><span className="t">UTR {payResult.utrNo || payResult.custUniqRef}</span></div>
                                        <CopyButton text={payResult.utrNo || payResult.custUniqRef} />
                                    </div>
                                    <p className="pg-note">Next: close this window and click “Mark as paid” to post the BP voucher.</p>
                                </>
                            )}
                            {payStep === 'pending' && payResult && (
                                <div key="pending" className="pg-res pg-res-warn">
                                    <div className="pg-ico">
                                        <div className="pg-ico-c">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path className="pg-clock-hand" d="M12 12V6.5" /></svg>
                                        </div>
                                    </div>
                                    <div><b>Pending at the bank</b><span className="t">{payResult.statusDescription || 'Check again in a moment.'}</span></div>
                                </div>
                            )}
                            {payStep === 'failed' && (
                                <div key="failed" className="pg-res pg-res-bad">
                                    <div className="pg-ico"><div className="pg-ico-c"><IconClose size={22} sw={3} color="#fff" /></div></div>
                                    <div><b>Payment failed</b><span className="t">{payError || 'Something went wrong. Try again.'}</span></div>
                                </div>
                            )}

                            <div className="pg-actions">
                                {payStep === 'review' && (
                                    <>
                                        <button className="pg-btn" onClick={closePayDialog}>Cancel</button>
                                        {missing.length === 0 && (
                                            <button className="pg-btn pg-btn-primary" onClick={handlePay}>
                                                Pay ₹ {money(payItem.amount)} <IconArrow size={16} />
                                            </button>
                                        )}
                                    </>
                                )}
                                {busy && <button className="pg-btn" disabled>Processing…</button>}
                                {payStep === 'pending' && (
                                    <>
                                        <button className="pg-btn" onClick={closePayDialog}>Close</button>
                                        <button className="pg-btn pg-btn-primary" onClick={() => checkPaymentStatus(payResult.custUniqRef)}>Check status again</button>
                                    </>
                                )}
                                {payStep === 'failed' && (
                                    <>
                                        <button className="pg-btn" onClick={closePayDialog}>Close</button>
                                        <button className="pg-btn pg-btn-primary" onClick={() => setPayStep('review')}>Try again</button>
                                    </>
                                )}
                                {payStep === 'done' && (
                                    <button className="pg-btn pg-btn-primary" onClick={closePayDialog}>Done</button>
                                )}
                            </div>

                            <div className="pg-secure"><IconShield size={14} /> Sent through your bank’s secure API</div>
                        </div>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default OtherPurchasePaymentPaid;




